import React, { createContext, useContext, useState, useEffect } from 'react';
import { TeamMember } from '../types';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { supabase } from '../supabaseClient';

interface TeamContextType {
  teamMembers: TeamMember[];
  addTeamMember: (member: Omit<TeamMember, 'id'>) => Promise<void>;
  updateTeamMember: (member: TeamMember) => Promise<void>;
  deleteTeamMember: (id: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  getTeamMemberById: (id: string) => TeamMember | undefined;
  getActiveTeamMembers: () => TeamMember[];
  currentUser: TeamMember | null;
  setCurrentUser: (user: TeamMember) => void;
  fetchTeamMembers: () => Promise<void>;
  // Impersonation (Act as User)
  impersonatedUser: TeamMember | null;
  isImpersonating: boolean;
  actualUser: TeamMember | null;
  startImpersonation: (member: TeamMember) => void;
  stopImpersonation: () => void;
  getEffectiveUser: () => TeamMember | null; // Returns impersonated user if active, otherwise current user
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

export const TeamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization, refreshOrganizations } = useOrganization();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(() => {
    return [];
  });

  const [currentUser, setCurrentUser] = useState<TeamMember | null>(() => {
    return null;
  });

  // Impersonation state - for admins/owners to act as other users
  const [actualUser, setActualUser] = useState<TeamMember | null>(null); // The real authenticated user
  const [impersonatedUser, setImpersonatedUser] = useState<TeamMember | null>(null); // The user being impersonated

  // Fetch team members from Supabase when authenticated and org is selected
  useEffect(() => {
    if (isAuthenticated && currentOrganization) {
      fetchTeamMembers();
    }
  }, [isAuthenticated, currentOrganization?.id]);

  // Set up real-time subscription to organization_members for automatic refresh
  useEffect(() => {
    if (!isAuthenticated || !currentOrganization) return;

    console.log('🔔 Setting up real-time subscription for organization_members');

    const channel = supabase
      .channel(`organization_members:${currentOrganization.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'organization_members',
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        (payload) => {
          console.log('📢 Real-time event received:', payload.eventType, payload);
          // Refresh team members when organization_members changes
          fetchTeamMembers();
        }
      )
      .subscribe((status) => {
        console.log('📡 Subscription status:', status);
      });

    // Cleanup subscription on unmount
    return () => {
      console.log('🔕 Unsubscribing from real-time channel');
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, currentOrganization?.id]);

  const fetchTeamMembers = async () => {
    if (!currentOrganization) {
      console.log('⚠️ TeamContext: No organization selected, skipping team fetch');
      return;
    }

    console.log('🔍 TeamContext: Fetching team members for org:', currentOrganization.id, currentOrganization.name);

    try {
      // Step 1: Get organization members (with their roles)
      const { data: orgMembers, error: membersError } = await supabase
        .from('organization_members')
        .select('user_id, role, joined_at')
        .eq('organization_id', currentOrganization.id)
        .limit(100);

      console.log('📊 TeamContext: Org members query result:', { orgMembers, membersError });

      if (membersError) {
        console.error('❌ TeamContext: Error fetching org members:', membersError);
        return;
      }

      if (!orgMembers || orgMembers.length === 0) {
        console.warn('⚠️ TeamContext: No members found for organization:', currentOrganization.id);
        setTeamMembers([]);
        return;
      }

      console.log('✅ TeamContext: Found', orgMembers.length, 'org members');

      // Step 2: Get profiles for those users
      const userIds = orgMembers.map((m: any) => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, email, hourly_rate, skills, is_active, joined_date')
        .in('id', userIds);

      console.log('📊 TeamContext: Profiles query result:', { profiles, profilesError });

      if (profilesError) {
        console.error('❌ TeamContext: Error fetching profiles:', profilesError);
        return;
      }

      if (!profiles || profiles.length === 0) {
        console.warn('⚠️ TeamContext: No profiles found for org members');
        setTeamMembers([]);
        return;
      }

      console.log('✅ TeamContext: Found', profiles.length, 'profiles');

      // Step 3: Combine the data
      const members: TeamMember[] = profiles.map((profile: any) => {
        const orgMember = orgMembers.find((m: any) => m.user_id === profile.id);
        return {
          id: profile.id,
          name: profile.name,
          email: profile.email,
          role: orgMember?.role || 'technician', // Use role from organization_members
          hourlyRate: profile.hourly_rate || 75,
          skills: profile.skills || [],
          isActive: profile.is_active !== false,
          joinedDate: profile.joined_date || orgMember?.joined_at || new Date().toISOString()
        };
      });

      setTeamMembers(members);

      // Set current user based on authenticated user
      if (user) {
        console.log('👤 TeamContext: Looking for current user:', user.id);
        console.log('👥 TeamContext: Team members:', members.map(m => ({ id: m.id, name: m.name })));
        
        const currentUserProfile = members.find(m => m.id === user.id);
        if (currentUserProfile) {
          console.log('✅ TeamContext: Found current user:', currentUserProfile.name);
          setCurrentUser(currentUserProfile);
          // Always set actualUser to the authenticated user (even if impersonating)
          setActualUser(currentUserProfile);
        } else {
          console.warn('⚠️ TeamContext: Current user not found in team members, will retry...');
          // If no profile found for current user, they might be newly created
          // Fetch again after a short delay
          setTimeout(() => {
            fetchTeamMembers();
          }, 1000);
        }
      } else {
        console.warn('⚠️ TeamContext: No authenticated user');
      }
    } catch (error) {
      console.error('Error in fetchTeamMembers:', error);
    }
  };

  const addTeamMember = async (member: Omit<TeamMember, 'id'>) => {
    if (!currentOrganization) {
      console.error('No organization selected');
      return;
    }

    try {
      // First, create the profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .insert({
          name: member.name,
          email: member.email,
          hourly_rate: member.hourlyRate,
          skills: member.skills,
          is_active: member.isActive,
          joined_date: member.joinedDate
        })
        .select()
        .single();

      if (profileError) {
        console.error('Error adding profile:', profileError);
        throw profileError;
      }

      // Then, add them to the organization
      const { error: orgError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: currentOrganization.id,
          user_id: profileData.id,
          role: member.role,
          joined_at: member.joinedDate
        });

      if (orgError) {
        console.error('Error adding to organization:', orgError);
        // Rollback: delete the profile if org insert fails
        await supabase.from('profiles').delete().eq('id', profileData.id);
        throw orgError;
      }

      // Refresh team members list to get the new member
      await fetchTeamMembers();
    } catch (error) {
      console.error('Error in addTeamMember:', error);
      throw error;
    }
  };

  const updateTeamMember = async (member: TeamMember) => {
    if (!currentOrganization) {
      console.error('No organization selected');
      return;
    }

    try {
      // Update profile (email is managed by auth.users, so we don't update it here)
      // Build update object with only defined values
      const updateData: any = {
        name: member.name,
        hourly_rate: member.hourlyRate,
        skills: member.skills || [],
        is_active: member.isActive !== undefined ? member.isActive : true
      };

      // Only include joined_date if it exists and is valid
      if (member.joinedDate) {
        updateData.joined_date = member.joinedDate;
      }

      console.log('Updating profile with data:', { id: member.id, updateData });

      const { error: profileError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', member.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        const errorMessage = profileError.message || profileError.details || profileError.hint || 'Failed to update profile';
        throw new Error(errorMessage);
      }

      // Update role in organization_members
      const { error: orgError } = await supabase
        .from('organization_members')
        .update({ role: member.role })
        .eq('organization_id', currentOrganization.id)
        .eq('user_id', member.id);

      if (orgError) {
        console.error('Error updating organization role:', orgError);
        const errorMessage = orgError.message || orgError.details || orgError.hint || 'Failed to update organization role';
        throw new Error(errorMessage);
      }

      // Refresh team members list to get updated data
      await fetchTeamMembers();

      // Update current user if it's the same person (use member parameter since state may not be updated yet)
      if (currentUser && currentUser.id === member.id) {
        setCurrentUser(member);
      }

      // Also update actualUser and impersonatedUser if needed
      if (actualUser && actualUser.id === member.id) {
        setActualUser(member);
      }
      if (impersonatedUser && impersonatedUser.id === member.id) {
        setImpersonatedUser(member);
      }
    } catch (error) {
      console.error('Error in updateTeamMember:', error);
      throw error;
    }
  };

  const deleteTeamMember = async (id: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    if (!currentOrganization) {
      console.error('No organization selected');
      return { success: false, error: 'No organization selected' };
    }

    try {
      console.log('🗑️ Deleting team member:', id, 'from organization:', currentOrganization.id);

      // Check if deleting the current authenticated user
      const isDeletingSelf = user && user.id === id;

      // Use RPC function to safely remove member (avoids RLS recursion)
      const { data, error } = await supabase.rpc('remove_organization_member', {
        p_organization_id: currentOrganization.id,
        p_user_id: id
      });

      if (error) {
        console.error('❌ Error calling remove_organization_member:', error);
        return { success: false, error: error.message };
      }

      // Check the response from the function
      if (data && !data.success) {
        console.error('❌ Function returned error:', data.error);
        return { success: false, error: data.error || 'Failed to remove team member' };
      }

      console.log('✅ Team member removed from organization:', data);

      // Refresh team members list to get updated data
      await fetchTeamMembers();

      // Clear current user if they were deleted
      if (currentUser && currentUser.id === id) {
        setCurrentUser(null);
      }

      // If deleting self, refresh organizations to remove it from the dropdown
      if (isDeletingSelf) {
        console.log('🔄 Current user was removed from organization, refreshing organizations list...');
        await refreshOrganizations();
      }

      return { success: true, message: 'Team member removed successfully' };
    } catch (error) {
      console.error('❌ Error in deleteTeamMember:', error);
      return { success: false, error: 'An unexpected error occurred while removing the team member.' };
    }
  };

  useEffect(() => {
    // No longer need to save to localStorage
  }, [teamMembers]);

  useEffect(() => {
    // No longer need to save to localStorage
  }, [currentUser]);


  const getTeamMemberById = (id: string) => {
    return teamMembers.find(member => member.id === id);
  };

  const getActiveTeamMembers = () => {
    return teamMembers.filter(member => member.isActive);
  };

  // Impersonation functions
  const startImpersonation = (member: TeamMember) => {
    if (!actualUser) {
      console.warn('⚠️ Cannot start impersonation: actual user not set');
      return;
    }
    
    // Only allow admins and owners to impersonate
    if (actualUser.role !== 'owner' && actualUser.role !== 'admin') {
      console.warn('⚠️ Only admins and owners can impersonate team members');
      return;
    }
    
    // Don't allow impersonating yourself
    if (member.id === actualUser.id) {
      console.warn('⚠️ Cannot impersonate yourself');
      return;
    }
    
    setImpersonatedUser(member);
    setCurrentUser(member); // Switch the current user view
    localStorage.setItem('impersonatedUserId', member.id);
    localStorage.setItem('actualUserId', actualUser.id);
    console.log('✅ Started impersonation:', member.name, 'as', actualUser.name);
  };

  const stopImpersonation = () => {
    if (!actualUser) {
      console.warn('⚠️ Cannot stop impersonation: actual user not set');
      return;
    }
    
    setImpersonatedUser(null);
    setCurrentUser(actualUser); // Switch back to actual user
    localStorage.removeItem('impersonatedUserId');
    localStorage.removeItem('actualUserId');
    console.log('✅ Stopped impersonation, returned to:', actualUser.name);
  };

  const getEffectiveUser = () => {
    // Return impersonated user if active, otherwise return current user
    return impersonatedUser || currentUser;
  };

  // Restore impersonation state on mount
  useEffect(() => {
    if (actualUser && teamMembers.length > 0) {
      const savedImpersonatedId = localStorage.getItem('impersonatedUserId');
      const savedActualId = localStorage.getItem('actualUserId');
      
      if (savedImpersonatedId && savedActualId === actualUser.id) {
        const memberToImpersonate = teamMembers.find(m => m.id === savedImpersonatedId);
        if (memberToImpersonate) {
          setImpersonatedUser(memberToImpersonate);
          setCurrentUser(memberToImpersonate);
        } else {
          // Clear invalid impersonation state
          localStorage.removeItem('impersonatedUserId');
          localStorage.removeItem('actualUserId');
        }
      }
    }
  }, [actualUser, teamMembers]);

  return (
    <TeamContext.Provider value={{
      teamMembers,
      addTeamMember,
      updateTeamMember,
      deleteTeamMember,
      getTeamMemberById,
      getActiveTeamMembers,
      currentUser,
      setCurrentUser,
      fetchTeamMembers,
      impersonatedUser,
      isImpersonating: !!impersonatedUser,
      actualUser,
      startImpersonation,
      stopImpersonation,
      getEffectiveUser
    }}>
      {children}
    </TeamContext.Provider>
  );
};